import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Zap } from "lucide-react";
import PostunHazirLogo from "../PostunHazirLogo";
import { useLanguage } from "../../contexts/LanguageContext";

/**
 * Inline blue-themed Postun Hazır CTA block — embedded inside Yastık Medya
 * service landing pages (creative + meta-ads). Visually distinct from the
 * pink Yastık Medya CTAs.
 */
export default function PostunHazirCTA() {
  const { t, lang } = useLanguage();

  return (
    <section
      data-testid="postun-hazir-cta"
      className="relative overflow-hidden bg-postun-blue-deep text-cream py-section"
    >
      {/* Soft cyan glow */}
      <div className="pointer-events-none absolute -top-32 end-[-12%] h-[420px] w-[420px] rounded-full bg-postun-cyan/30 blur-3xl" />
      <div className="pointer-events-none absolute -bottom-28 start-[-8%] h-[360px] w-[360px] rounded-full bg-postun-blue-glow/40 blur-3xl" />

      <div className="container-page relative">
        <div className="grid items-center gap-10 lg:grid-cols-12">
          <div className="lg:col-span-7">
            <PostunHazirLogo asLink={false} tone="cream" size={28} />
            <h2
              className="mt-6 font-display text-display-2 font-semibold leading-[1.02]"
              data-testid="postun-hazir-cta-title"
            >
              {t("postun_hazir.cta_title_pre")}{" "}
              <span
                className="bg-clip-text text-transparent"
                style={{
                  backgroundImage:
                    "linear-gradient(120deg, #FCD34D 0%, #7CEDD9 50%, #FFFFFF 100%)",
                }}
              >
                {t("postun_hazir.cta_title_accent")}
              </span>
            </h2>
            <p className="mt-5 max-w-xl text-body-lg text-cream/85 leading-relaxed">
              {t("postun_hazir.cta_subtitle")}
            </p>

            <div className="mt-8 flex flex-wrap items-center gap-4">
              <Link
                to={`/${lang}/postun-hazir`}
                className="inline-flex items-center gap-2 rounded-full
                           bg-postun-yellow text-postun-blue-deep
                           px-7 py-3.5 text-sm font-semibold
                           transition-[transform,box-shadow] duration-300 ease-out-quart
                           hover:shadow-[0_18px_50px_-15px_rgba(252,211,77,0.7)] hover:scale-[1.02]
                           active:scale-[0.98]"
                data-testid="postun-hazir-cta-primary"
              >
                <Zap className="h-4 w-4" />
                {t("postun_hazir.cta_primary")}
                <ArrowUpRight className="h-4 w-4" />
              </Link>
              <a
                href="https://instagram.com/postunhazir"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 rounded-full border-2 border-cream/30
                           bg-transparent text-cream px-7 py-3.5 text-sm font-semibold
                           transition-colors duration-300
                           hover:border-cream hover:bg-cream hover:text-postun-blue-deep"
              >
                {t("postun_hazir.cta_secondary")}
              </a>
            </div>
          </div>

          {/* Stats bullets */}
          <div className="lg:col-span-5">
            <ul className="space-y-4 text-sm">
              {["fast", "branded", "scalable", "affordable"].map((k) => (
                <li
                  key={k}
                  className="flex items-start gap-3 rounded-2xl bg-postun-blue/40 backdrop-blur-sm border border-cream/10 px-5 py-3.5"
                >
                  <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-postun-yellow text-postun-blue-deep font-bold text-xs flex-shrink-0">
                    ✓
                  </span>
                  <span className="text-cream/90 leading-snug">
                    {t(`postun_hazir.bullets.${k}`)}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
}
